import { useState } from "react";
import clsx from "clsx";
import type { Alert, DealSummary, ActivityEvent } from "@edge/shared";
import { AlertsRail } from "./AlertsRail";
import { ActivityFeed } from "./ActivityFeed";
import type { CrmTaskCreateInput } from "../../hooks/useCrmTaskActions";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";
import type { SaveNoteInput } from "../shared/SaveNoteDialog";

type Tab = "watchtower" | "activity";

interface WatchtowerActivityPanelProps {
  // --- Watchtower (AlertsRail) ---
  alerts: Alert[];
  watchtowerLoading?: boolean;
  restoredNotes?: Map<string, { kind: "solved" | "removed"; note: string }>;
  onRefreshWatchtower?: () => Promise<void> | void;
  onFeedback?: (id: string, kind: "solved" | "removed", note?: string) => void;
  deals?: DealSummary[];
  onCreateCrmTask?: (input: CrmTaskCreateInput) => Promise<void>;
  onAddToMyList?: (input: PersonalTaskInput) => Promise<void>;
  onSaveNote?: (input: SaveNoteInput) => Promise<void>;
  // --- Live Activity (ActivityFeed) ---
  activity: ActivityEvent[];
  activityNoiseSuppressed?: number;
}

/**
 * CK-1 (b) — right-rail tabbed panel that now HOSTS the Watchtower (moved out
 * of center-stage) alongside the Live Activity feed. Two tabs:
 *   - WATCHTOWER (default): AlertsRail in its embedded variant.
 *   - LIVE ACTIVITY: ActivityFeed slimmed to its all-events view (no DEALS/ALL).
 * This RELOCATES Watchtower only — what it flags is unchanged (that's WT-1).
 */
export function WatchtowerActivityPanel({
  alerts,
  watchtowerLoading,
  restoredNotes,
  onRefreshWatchtower,
  onFeedback,
  deals,
  onCreateCrmTask,
  onAddToMyList,
  onSaveNote,
  activity,
  activityNoiseSuppressed,
}: WatchtowerActivityPanelProps) {
  const [tab, setTab] = useState<Tab>("watchtower");

  return (
    <div className="wt-activity-rail">
      <div className="wt-activity-panel">
        <span className="bracket tl" />
        <span className="bracket tr" />
        <span className="bracket bl" />
        <span className="bracket br" />

        <div className="wt-tabs">
          <button
            className={clsx("wt-tab", tab === "watchtower" && "active")}
            onClick={() => setTab("watchtower")}
          >
            <span className="wt-tab-marker" /> WATCHTOWER
            {alerts.length > 0 && (
              <span className="wt-tab-count">{alerts.length}</span>
            )}
          </button>
          <button
            className={clsx("wt-tab", tab === "activity" && "active")}
            onClick={() => setTab("activity")}
          >
            LIVE ACTIVITY
          </button>
          <span className="wt-tabs-spacer" />
          {tab === "watchtower" && onRefreshWatchtower && (
            <button
              className="watch-filter-pill refresh"
              onClick={onRefreshWatchtower}
              disabled={watchtowerLoading}
              title="Re-scan watchtower"
            >
              {watchtowerLoading ? "↻ SCANNING…" : "↻ REFRESH"}
            </button>
          )}
        </div>

        <div className="wt-tab-body">
          {tab === "watchtower" ? (
            <AlertsRail
              variant="embedded"
              alerts={alerts}
              restoredNotes={restoredNotes}
              loading={watchtowerLoading}
              onFeedback={onFeedback}
              deals={deals}
              onCreateCrmTask={onCreateCrmTask}
              onAddToMyList={onAddToMyList}
              onSaveNote={onSaveNote}
            />
          ) : (
            <ActivityFeed
              embedded
              events={activity}
              noiseSuppressed={activityNoiseSuppressed}
            />
          )}
        </div>
      </div>
    </div>
  );
}
